import {
	Conversation,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	SplitRow,
	Steps,
} from "@marketing/components/blocks";
import { AppVisual, BranchVisual } from "@marketing/components/visuals";
import { PhoneScreen } from "@marketing/hero/PhoneScreen";
import { STILL_FRAME } from "@marketing/hero/timeline";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "Which platforms does the SDK support?",
		a: "iOS and Android apps (native, Flutter and React Native) and any website. The assistant opens over your app and talks to it through a small bridge, so your developers add a few lines rather than build a chat.",
	},
	{
		q: "Does it use my app's cart?",
		a: "Yes. When the assistant adds or removes a product, your app receives the change and updates its own cart, with both the catalog id and the item id your store uses.",
	},
	{
		q: "Who handles checkout and payment?",
		a: "Your app. When the shopper wants to pay, the assistant hands the basket lines to your app, which opens its own checkout with its payments, delivery slots and loyalty.",
	},
	{
		q: "Can it look like my app?",
		a: "Colours, corners, buttons, fonts, the assistant's name and what is shown are set in your dashboard, and your app can override them at launch. It can follow your app's light or dark mode.",
	},
	{
		q: "Can my app change the wording in the chat?",
		a: "Yes. The welcome title, placeholder, button labels and the assistant's name can be replaced by your app through the bridge. Wording is never taken from the chat's URL, which anyone could edit.",
	},
	{
		q: "Do shoppers need to log in?",
		a: "No. Your app opens the assistant with a signed launch token for the shopper it already knows, so conversations and baskets follow the shopper across devices.",
	},
	{
		q: "What can we measure?",
		a: "Your dashboard shows conversations, top searches, searches with no results, languages and departments, plus orders and revenue linked to the assistant when you report orders through the API. Your app also receives the assistant's state and how long it took to open.",
	},
	{
		q: "Does it work in Arabic in the app?",
		a: "Yes. Shoppers type or speak in Arabic, Arabizi, English or French, and the assistant answers in the language and script they used. Your app can set the chat's language, such as Arabic for an Arabic app.",
	},
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "Birthday party for 15 kids, need snacks and drinks, under 300 AED",
	},
	{
		from: "assistant",
		text: "Here is a party list within 300 dirhams: juice boxes, crisps, popcorn, cups and plates.",
		note: "One row per item. A product shows only if it fits the budget with the rest of the list.",
	},
	{
		from: "shopper",
		text: "add the juice, 2 packs",
	},
	{
		from: "assistant",
		text: "Added 2 packs of juice to your cart.",
		note: "Your app receives the change with its own item id and updates its cart badge.",
	},
	{
		from: "shopper",
		text: "عندكم كيك جاهز؟",
		en: "Do you have ready-made cake?",
	},
	{
		from: "assistant",
		text: "إيه، هذي الكيكات المتوفرة في فرعك:",
		en: "Yes, here are the cakes your branch has:",
		note: "The shopper switched to Arabic, so the assistant did too.",
	},
	{
		from: "shopper",
		text: "(taps Checkout)",
		note: "Your app opens its own checkout with these lines. Payment, slots and loyalty stay in your app.",
	},
];

const BUILT_IN = [
	{
		title: "iOS, Android and web",
		text: "SDKs for native, Flutter and React Native apps, and one script tag for a website. Chat, voice and product cards come with it.",
	},
	{
		title: "Your cart, not ours",
		text: "Every add and remove is sent to your app, which updates its own cart and checkout as usual.",
	},
	{
		title: "Your look",
		text: "Colours, corners, buttons and fonts from your dashboard, overridable by your app at launch.",
	},
	{
		title: "Knows the shopper",
		text: "A signed launch token tells the assistant who is shopping, without another login.",
	},
	{
		title: "Voice included",
		text: "Dictation, voice notes and hands-free live voice, in Arabic and English.",
	},
	{
		title: "Opens where it helps",
		text: "From a button, the search bar, a product page or a recipe: wherever your shoppers get stuck. Your app can send it a question and follow what it does.",
	},
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "From Shopify, WooCommerce, a product feed or our API, with each branch's prices and stock, keyed on the ids your app already uses.",
	},
	{
		title: "Add the SDK",
		text: "The Install screen shows the code for your platform with your values filled in, and a test code to try it in a build.",
	},
	{
		title: "Style it and ship",
		text: "Set the look and wording in your dashboard, test it on your own catalog, then release it in your next app update.",
	},
];

const RELATED = [
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/channels/voice", label: "Voice ordering" },
	{ href: "/integrations", label: "Integrations" },
	{ href: "/how-it-works", label: "How search works" },
	{ href: "/shoppable-recipes", label: "Shoppable recipes" },
	{ href: "/ai-agent-for-ecommerce", label: "AI agent for ecommerce" },
];

export const Route = createFileRoute("/_marketing/channels/in-app")({
	head: () =>
		marketingHead({
			title: "Ecommerce AI Chatbot SDK for Apps | Hephon Commerce",
			description:
				"Add an AI chatbot for ecommerce to your grocery app. A drop-in SDK for iOS, Android and the web that searches your catalog and fills your app's own cart.",
			path: "/channels/in-app",
			breadcrumb: ["In-app assistant"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: InAppPage,
});

function InAppPage() {
	return (
		<>
			<PageIntro
				kicker="A shopping assistant inside your own app."
				title="An AI shopping assistant inside your own app"
				lead="It opens over your iOS, Android or web app, searches your catalog and adds to the cart your app already has, in your colours and your words."
			>
				<div className="mx-auto h-[420px] w-[318px] overflow-hidden rounded-t-[54px] bg-[linear-gradient(145deg,oklch(0.34_0.004_270),oklch(0.15_0.003_270)_45%,oklch(0.28_0.004_270))] p-[9px] pb-0 sm:h-[520px]">
					<div className="h-[652px] overflow-hidden rounded-t-[45px] bg-(--mk-white)">
						<PhoneScreen frame={STILL_FRAME} />
					</div>
				</div>
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce puts an AI shopping assistant inside a
					retailer's own app and website, with SDKs for iOS, Android,
					React Native and Flutter and a script tag for the web.
					Shoppers ask in Arabic or English; the assistant searches
					the live catalog with their branch's prices and adds
					products to the cart the app already has.
				</p>
				<p>
					The app keeps its checkout, payments and delivery slots. The
					retailer controls the assistant's colours, corners, light or
					dark mode and wording, and nothing is ordered without the
					shopper's confirmation.
				</p>
			</InShort>

			<Section
				id="built-in-title"
				title="Built into your app, not bolted on."
				lead="Your developers add the SDK; the assistant brings search, product cards, the basket and voice."
			>
				<DetailGrid items={BUILT_IN} />
			</Section>

			<Section
				id="chat-title"
				tone="mist"
				title="A shopper in your app, start to finish."
				lead="A budget, a list, a switch to Arabic, and your own checkout at the end."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="sdk">How does the SDK fit into my app?</h2>
				<p>
					There is one SDK per platform: a Swift package for iOS
					(UIKit and SwiftUI), a library for Android, a package for
					React Native with an Expo plugin, and a package for Flutter.
					A website adds one script tag. Each one opens the same
					assistant for one shopper, loads it before the shopper taps
					so it appears at once, and keeps it loaded between opens.
				</p>
				<p>
					The assistant runs in a web view on your store's chat page,
					managed by the SDK. Everything it does to the basket comes
					back to your app as typed callbacks in your own product ids,
					so your developers handle a few events instead of building a
					chat, a search and a product card from scratch. The Install
					screen in your dashboard shows the code for each platform
					with your values filled in, and a test code to try it.
				</p>

				<h2 id="cart">
					How does the basket stay in sync with my app's cart?
				</h2>
				<p>
					Every product has two of your ids: its id in the shopper's
					store, which your cart lines are keyed on, and its id in
					your catalog. When the assistant or the shopper adds,
					changes or removes a product, your app receives both ids and
					the quantity, and updates its own cart. Your app can also
					send its cart to the assistant, so a product the shopper
					added from your own screens is already in the basket when
					they open the chat. When the shopper taps checkout, your app
					opens its own checkout with the basket lines.
				</p>
			</Article>

			<Section
				id="basket-title"
				tone="mist"
				title="It shops inside your app. Checkout stays yours."
			>
				<div className="space-y-20">
					<SplitRow
						title="Your cart, kept in step"
						text="The assistant finds products and fills the basket. Your app keeps the checkout, payments, delivery slots and loyalty it already has."
						points={[
							"Both of your product ids on every change, so carts match exactly",
							"Works the same in Arabic and English",
						]}
						visual={<AppVisual />}
					/>
					<SplitRow
						reverse
						title="The store your app is showing"
						text="Prices and stock come from the branch the shopper is shopping in, so the assistant never offers what that store cannot deliver."
						visual={<BranchVisual className="mb-8" />}
					/>
				</div>
			</Section>

			<Article>
				<h2 id="look">Who controls how it looks?</h2>
				<p>
					You do. In your dashboard you set the colours, corners,
					button shapes, font, header and the assistant's name, and
					whether the chat is light, dark or follows the device. Your
					app can override any of these when it opens the chat, so the
					assistant matches the screen it sits in, and it can switch
					the chat to dark mode when the app does. Colours are applied
					so that text stays readable on your brand colour in both
					light and dark mode.
				</p>

				<h2 id="wording">Can my app change what the chat says?</h2>
				<p>
					Your app can replace the welcome title, the placeholder, the
					button labels and the assistant's name, and set the
					suggestions on the welcome screen. Wording goes through the
					SDK's bridge only, never through the chat's address: an
					address can be edited by anyone, while the bridge only
					accepts your app and the websites you allow. Your app can
					also send the assistant a question, open the basket, start a
					new chat or set its language.
				</p>

				<h2 id="analytics">What can we measure?</h2>
				<p>
					Your dashboard shows conversations day by day, what shoppers
					search for, the searches that found nothing, and the
					languages and departments they ask about. When your platform
					reports its orders through our API, the Conversions view
					shows which orders the assistant helped with and the revenue
					from them. Inside your app, the SDK tells you what the chat
					is doing (open, busy, items in the basket) and how long it
					took to open, for your own analytics.
				</p>

				<h2 id="login">Do shoppers need to log in again?</h2>
				<p>
					No. Your server signs a launch token for the shopper your
					app already knows, and the assistant opens for that shopper
					and their branch. Conversations and baskets then follow the
					shopper across devices. You can require a launch token for
					every chat, so no one can open your assistant anonymously.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						It does not take payment or replace your checkout. Your
						app's checkout, payment methods, delivery slots and
						loyalty stay as they are.
					</li>
					<li>
						It never places an order without the shopper's
						confirmation.
					</li>
					<li>
						It does not draw native screens you restyle line by
						line. The chat is a web view managed by the SDK, styled
						through the appearance settings.
					</li>
					<li>
						It does not use the microphone unless your app asks for
						the permission. Without it, the chat works by text.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						Your developers cannot ship an app update. The SDK is a
						few lines, but it goes out in a release of your app. The
						website script or WhatsApp is a faster start.
					</li>
					<li>
						You need a support bot for returns and complaints rather
						than a shopping assistant. This is built for shopping
						from a catalog.
					</li>
					<li>
						You have no online catalog with prices and stock per
						branch.
					</li>
				</ul>
			</Article>

			<Section
				id="website-title"
				tone="mist"
				title="On your website, one line."
				lead="The same assistant as a chat button on any site, with the same catalog, basket and look."
			>
				<pre className="mk-reveal overflow-x-auto rounded-[24px] bg-(--mk-night) p-6 font-mono text-(--mk-on-night) text-[13px] leading-relaxed sm:p-8 sm:text-[14px]">
					<code>
						{
							'<script src="https://commerce.hephon.ai/widget/v1.js"\n        data-org="your-store" async></script>'
						}
					</code>
				</pre>
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="In-app assistant, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
